import React, { useRef } from "react";
import { Calendar } from "lucide-react";
import { linkifyText } from "../ACS-Utils/textFormatting";

export const DisplayDetail = ({
  label,
  value,
  onChange,
  onClick,
  onBlur,

  isEditable = false,
  isReadOnly = false,
  className = "",
  icon,
  inputType = "text",
  showCalendar = false,
  multiline = false, // multi-line text (like notes)
  autoLink = false, // enable automatic linking
  /** Draws attention to a field that still needs to be filled in (e.g. required-before-checkout) — not an error state, just a "this one's next" cue. */
  highlighted = false,
  required = false,
  placeholder,

}) => {
  const displayValue = value != null && value !== "" ? value : "—";
  const inputRef = useRef(null);

  return (
    <div className={`flex flex-col min-w-[clamp(8rem,12vw,10rem)] ${className}`}>
      <span className="text-dense-xs text-gray-500 dark:text-acs-dark-subtext">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      <div
        className={`relative flex items-center justify-between w-full
                   px-fluid-2 py-fluid-1.5 mt-fluid-0.25
                   ${
                     isEditable && isReadOnly
                       ? "bg-gray-200 dark:bg-acs-dark-surface text-gray-600 dark:text-acs-dark-subtext cursor-not-allowed"
                       : highlighted
                         ? "bg-amber-50 dark:bg-amber-900/10 text-gray-700 dark:text-acs-dark-text"
                         : "bg-[#FAFAFB] dark:bg-acs-dark-surface text-gray-700 dark:text-acs-dark-text"
                   }
                   rounded-md border ${highlighted ? "border-amber-400 dark:border-amber-500" : "border-gray-300 dark:border-acs-dark-border"}
                   text-fluid-sm font-inter
                   focus-within:ring-2 focus-within:ring-blue-400 dark:focus-within:ring-[#223688]
                   focus-within:border-blue-400 dark:focus-within:border-[#223688]
                   transition-none ease-in-out
                   ${multiline && (!isEditable || isReadOnly) ? 'items-start' : ''}`}
      >
        {isEditable && !isReadOnly ? (
          <>
            {multiline ? (
              <textarea
                ref={inputRef}
                value={value || ""}
                onChange={(e) => onChange && onChange(e.target.value)}
                placeholder={placeholder}

                className="w-full bg-transparent outline-none focus:outline-none dark:text-acs-dark-text resize-y min-h-[80px]"
              />
            ) : (
              <input
                ref={inputRef}
                type={inputType}
                value={value || ""}
                onChange={(e) => onChange && onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}

                className={`w-full bg-transparent outline-none focus:outline-none dark:text-acs-dark-text ${
                  showCalendar ? "pr-fluid-5" : ""
                }`}
              />
            )}
            {showCalendar && !multiline && (
              <button
                type="button"
                onClick={() => inputRef.current?.showPicker()}
                className="absolute right-fluid-2 top-1/2 -translate-y-1/2 
                           text-gray-500 dark:text-acs-dark-subtext 
                           hover:text-blue-600 dark:hover:text-[#3B5CCC]
                           transition-none"
              >
                <Calendar size={16} />
              </button>
            )}
          </>
        ) : (
          // LGX-1136: a truncated value (e.g. a long "LAY - Layover" charge name) had no way to
          // reveal its full text -- title is a no-op when the text already fits, so this is safe
          // for every existing DisplayDetail usage, not just the Charge Details panel.
          <span
            className={multiline ? "whitespace-pre-wrap w-full" : "truncate"}
            title={typeof displayValue === "string" ? displayValue : undefined}
          >
            {autoLink && displayValue !== "—" ? linkifyText(displayValue) : displayValue}
          </span>
        )}
        {icon && !showCalendar && (
          onClick ? (
            <button
              type="button"
              onClick={onClick}
              className="ml-fluid-0.75 flex-shrink-0"
              aria-label={`Edit ${label}`}
            >
              {icon}
            </button>
          ) : (
            <span className="ml-fluid-0.75 flex-shrink-0">{icon}</span>
          )
        )}
      </div>
    </div>
  );
};